export type ApplicationErrorCode =
  | 'data-invalid'
  | 'provider-unavailable'
  | 'provider-rejected'
  | 'storage-unavailable'
  | 'unexpected';

export type ErrorSeverity = 'notice' | 'warning' | 'fatal';

export type ErrorMessageId =
  | 'error.data.invalid'
  | 'error.provider.rejected'
  | 'error.provider.unavailable'
  | 'error.storage.unavailable'
  | 'error.unexpected';

export type DiagnosticKey = 'attempt' | 'httpStatus' | 'operation' | 'provider';

export interface ApplicationDiagnostics {
  readonly attempt?: number;
  readonly httpStatus?: number;
  readonly operation?: string;
  readonly provider?: string;
}

export interface ApplicationError {
  readonly code: ApplicationErrorCode;
  readonly severity: ErrorSeverity;
  readonly retryable: boolean;
  readonly messageId: ErrorMessageId;
  readonly diagnostics: Readonly<ApplicationDiagnostics>;
}

export type ProviderId =
  'analytics' | 'esri' | 'openfreemap' | 'swisstopo' | 'terrain' | 'trail-status' | 'zugerwald';
export type ProviderOperation = 'load' | 'refresh' | 'render';

const safeDiagnosticText = /^[a-z0-9-]{1,40}$/u;

export const applicationErrorPolicy = Object.freeze({
  'data-invalid': {
    severity: 'fatal',
    retryable: false,
    messageId: 'error.data.invalid',
  },
  'provider-unavailable': {
    severity: 'warning',
    retryable: true,
    messageId: 'error.provider.unavailable',
  },
  'provider-rejected': {
    severity: 'warning',
    retryable: false,
    messageId: 'error.provider.rejected',
  },
  'storage-unavailable': {
    severity: 'notice',
    retryable: false,
    messageId: 'error.storage.unavailable',
  },
  unexpected: {
    severity: 'fatal',
    retryable: false,
    messageId: 'error.unexpected',
  },
} satisfies Readonly<
  Record<
    ApplicationErrorCode,
    {
      readonly severity: ErrorSeverity;
      readonly retryable: boolean;
      readonly messageId: ErrorMessageId;
    }
  >
>);

function validateDiagnosticValue(key: DiagnosticKey, value: number | string): number | string {
  if (key === 'attempt' || key === 'httpStatus') {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > 999_999) {
      throw new RangeError(`unsafe diagnostic value for ${key}`);
    }
    return value;
  }
  if (typeof value !== 'string' || !safeDiagnosticText.test(value)) {
    throw new TypeError(`unsafe diagnostic value for ${key}`);
  }
  return value;
}

export function createApplicationError(input: {
  readonly code: ApplicationErrorCode;
  readonly diagnostics?: Readonly<ApplicationDiagnostics>;
}): ApplicationError {
  const diagnostics: {
    attempt?: number;
    httpStatus?: number;
    operation?: string;
    provider?: string;
  } = {};
  for (const key of ['attempt', 'httpStatus', 'operation', 'provider'] as const) {
    const value = input.diagnostics?.[key];
    if (value !== undefined) {
      if (key === 'attempt' || key === 'httpStatus') {
        diagnostics[key] = validateDiagnosticValue(key, value) as number;
      } else {
        diagnostics[key] = validateDiagnosticValue(key, value) as string;
      }
    }
  }
  return Object.freeze({
    code: input.code,
    ...applicationErrorPolicy[input.code],
    diagnostics: Object.freeze(diagnostics),
  });
}

export function classifyProviderFailure(
  failure: unknown,
  context: {
    readonly provider: ProviderId;
    readonly operation: ProviderOperation;
    readonly httpStatus?: number;
  },
): ApplicationError {
  // Provider exceptions are deliberately ignored so URLs, response bodies, and locations cannot leak.
  void failure;
  const rejected =
    context.httpStatus !== undefined && context.httpStatus >= 400 && context.httpStatus < 500;
  return createApplicationError({
    code: rejected ? 'provider-rejected' : 'provider-unavailable',
    diagnostics: {
      provider: context.provider,
      operation: context.operation,
      ...(context.httpStatus === undefined ? {} : { httpStatus: context.httpStatus }),
    },
  });
}
